import { Fragment, useEffect, useState, type ReactElement } from "react";
import { setDisplayCurrency } from "@iltocat/shared";
import { AppShell } from "./components/AppShell.js";
import { Logo } from "./components/Logo.js";
import { Notice } from "./components/Notice.js";
import { ToastProvider } from "./components/Toast.js";
import { plural } from "./lib/api.js";
import { drainQueue } from "./lib/offlineQueue.js";
import { cameFrom, linkOutcomeFrom, pathOf, useRouter, type LinkOutcome, type Route } from "./lib/router.js";
import { restoreScroll } from "./lib/freshness.js";
import { useFreshness } from "./lib/useFreshness.js";
import { useSession } from "./lib/useSession.js";
import { useTaxonomy } from "./lib/useTaxonomy.js";
import { useVocabulary } from "./lib/useVocabulary.js";
import { Settings } from "./routes/Settings.js";
import { Admin } from "./routes/Admin.js";
import { Billing } from "./routes/Billing.js";
import { Capture } from "./routes/Capture.js";
import { Clients } from "./routes/Clients.js";
import { Enrich } from "./routes/Enrich.js";
import { Export } from "./routes/Export.js";
import { ItemScreen } from "./routes/ItemScreen.js";
import { Library } from "./routes/Library.js";
import { Listing } from "./routes/Listing.js";
import { Import } from "./routes/Import.js";
import { Labels } from "./routes/Labels.js";
import { Locations } from "./routes/Locations.js";
import { Receive } from "./routes/Receive.js";
import { Reports } from "./routes/Reports.js";
import { Scan } from "./routes/Scan.js";
import { Sort } from "./routes/Sort.js";
import { Sharing } from "./routes/Sharing.js";
import { SignIn } from "./routes/SignIn.js";
import { Stock } from "./routes/Stock.js";
import { Reserves } from "./routes/Reserves.js";

/**
 * Wiring only: who is signed in, which screen that means, and the two messages
 * the worker sends back through the URL after a magic link.
 *
 * The signed-out states have no shell around them: the sign-in page is its
 * own full-bleed layout, and a sidebar with nothing in it would only say
 * "there is an app behind this door" without letting anybody in.
 *
 * The one thing held here rather than per screen is the taxonomy (locations
 * and categories). Capture, the item screen and the shelf view all need it, it
 * changes when any of them writes, and fetching it three times would mean
 * three copies disagreeing about what a shelf is called.
 */
export function App() {
  const session = useSession();
  const { route, go, back, visit } = useRouter();
  const [query] = useState(() => new URLSearchParams(window.location.search));
  // Held here rather than read by Settings, so it is said once: the address
  // is wiped below, and Settings can be left and come back to.
  const [linkOutcome, setLinkOutcome] = useState(() => linkOutcomeFrom(query));

  // The worker redirects to `/?signin=ok` and `/signin?error=...`. Both are read
  // once and then wiped, so a reload does not replay a message about something
  // that happened minutes ago.
  useEffect(() => {
    // Except on the search and enrich screens, whose state is what is after the ?.
    if (
      window.location.search &&
      !window.location.pathname.startsWith("/search") &&
      !window.location.pathname.startsWith("/enrich")
    ) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  if (session.status === "loading") {
    return (
      <div className="splash" aria-live="polite">
        <Logo size="lg" withWord={false} />
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  if (session.unconfigured) {
    return (
      <div className="splash">
        <div className="panel splash-card">
          <Logo />
          <h1 className="panel-title">Not configured</h1>
          <Notice tone="error">{`Missing: ${session.unconfigured.join(", ")}.`}</Notice>
          <p className="muted">
            Secrets or database migrations are not in place yet. See docs/notes/deploying.md.
          </p>
        </div>
      </div>
    );
  }

  if (!session.user) {
    return (
      <SignIn initialError={query.get("error")} linkToken={query.get("token")} onSignedIn={session.refresh} />
    );
  }

  // A last chance to send what is still waiting, and a plain warning rather
  // than a silent loss if signing out would leave something behind: an
  // offline queue is per device, not per account, and useSession.signOut()
  // clears it the moment the sign-out actually happens. Signing out everywhere
  // ends this device's session too, so it gets the same warning.
  async function handleSignOut(everywhere = false) {
    const { remaining } = await drainQueue();
    if (remaining > 0) {
      const pronoun = remaining === 1 ? "it" : "them";
      const ok = window.confirm(
        `${plural(remaining, "item")} from this device ${remaining === 1 ? "is" : "are"} still waiting to ` +
          `upload. Signing out now will lose ${pronoun}. Sign out anyway?`,
      );
      if (!ok) return;
    }
    await session.signOut(everywhere);
  }

  return (
    <ToastProvider>
      <AppShell
        user={session.user}
        route={route}
        onNavigate={go}
        onSignOut={() => void handleSignOut()}
        onStorage={session.setUsedBytes}
      >
        <SignedIn
          route={route}
          go={go}
          back={back}
          visit={visit}
          user={session.user}
          setUsedBytes={session.setUsedBytes}
          isAdmin={session.user.isAdmin}
          linkOutcome={linkOutcome}
          onLinkOutcomeSeen={() => setLinkOutcome(null)}
          onSignOutEverywhere={() => void handleSignOut(true)}
          onRefetch={() => void session.refresh()}
        />
      </AppShell>
    </ToastProvider>
  );
}

/**
 * The signed-in screens, split out for one reason: `useTaxonomy` fetches, and a
 * hook cannot be called conditionally. Keeping it below the sign-in check means
 * a signed-out visitor never asks for locations they do not have.
 */
function SignedIn({
  route,
  go,
  back,
  visit,
  user,
  setUsedBytes,
  isAdmin,
  linkOutcome,
  onLinkOutcomeSeen,
  onSignOutEverywhere,
  onRefetch,
}: {
  route: Route;
  go: (route: Route) => void;
  back: () => void;
  visit: number;
  user: NonNullable<ReturnType<typeof useSession>["user"]>;
  setUsedBytes: (bytes: number) => void;
  isAdmin: boolean;
  linkOutcome: LinkOutcome | null;
  onLinkOutcomeSeen: () => void;
  onSignOutEverywhere: () => void;
  /** Re-read the session: storage used, inventories, still signed in at all. */
  onRefetch: () => void;
}) {
  // Before anything below renders an amount: every screen writes money in the
  // active inventory's currency, and an effect would paint dollars first.
  setDisplayCurrency(user.activeCurrency);
  const taxonomy = useTaxonomy();
  const stockroom = user.activeKind === "stock";
  const vocabulary = useVocabulary(stockroom);
  const openItem = (id: string) => go({ name: "item", id });

  // Coming back to the app after a while re-fetches the screen in view by
  // remounting it, along with what is held here (lib/freshness.ts). Where the
  // page was scrolled to survives it.
  const [fresh, setFresh] = useState(0);
  useFreshness(() => {
    const y = window.scrollY;
    onRefetch();
    void taxonomy.reload();
    void vocabulary.reloadTags();
    if (stockroom) void vocabulary.reloadReserveTags();
    setFresh((n) => n + 1);
    restoreScroll(y);
  });

  // Home and search are one screen since v2: the library, with its state in
  // the address. It keeps its state while somebody types (which rewrites the
  // address without navigating) and starts fresh on every deliberate visit to
  // home, such as pressing Items with a search still showing.
  const library = (
    <Library
      key={route.name === "search" ? "search" : `home-${visit}`}
      user={user}
      taxonomy={taxonomy}
      vocabulary={vocabulary}
      onOpen={openItem}
      onCapture={() => go({ name: "capture" })}
      onSort={() => go({ name: "sort" })}
      onLabels={(ids) => go({ name: "labels", kind: "item", ids })}
      onScanLabel={(target) =>
        target.kind === "item" ? openItem(target.id) : go({ name: "locations", id: target.id })
      }
    />
  );

  return <Fragment key={fresh}>{screen()}</Fragment>;

  function screen(): ReactElement {
    switch (route.name) {
      // Not an error screen for a non-admin: the console is meant to be
      // indistinguishable from a path that does not exist, and its routes already
      // answer 404. Landing on the app is what a wrong address does everywhere
      // else here.
      case "admin":
        return isAdmin ? <Admin /> : library;

      case "sharing":
        return <Sharing user={user} />;

      case "settings":
        return (
          <Settings
            user={user}
            onNavigate={go}
            linkOutcome={linkOutcome}
            onLinkOutcomeSeen={onLinkOutcomeSeen}
            onSignOutEverywhere={onSignOutEverywhere}
          />
        );

      case "export":
        return <Export user={user} taxonomy={taxonomy} vocabulary={vocabulary} />;

      case "reports":
        return <Reports user={user} taxonomy={taxonomy} vocabulary={vocabulary} onOpen={openItem} />;

      case "import":
        return <Import user={user} taxonomy={taxonomy} onItems={() => go({ name: "home" })} />;

      case "scan":
        return (
          <Scan
            onTarget={(target) => {
              if (target.kind === "item") go({ name: "item", id: target.id });
              else if (target.kind === "location") go({ name: "locations", id: target.id });
              else go({ name: "search", query: `q=${encodeURIComponent(target.text)}` });
            }}
          />
        );

      case "labels":
        return (
          <Labels kind={route.kind} ids={route.ids} taxonomy={taxonomy} stock={stockroom} onBack={back} />
        );

      // Stage 7's screens. In a personal inventory they are the same wrong
      // address anything else is: the library, not a screen saying "not here".
      case "stock":
        return stockroom ? (
          <Stock
            taxonomy={taxonomy}
            vocabulary={vocabulary}
            onOpen={openItem}
            onLabels={(ids) => go({ name: "labels", kind: "item", ids })}
            onReceive={() => go({ name: "receive" })}
            onReserves={() => go({ name: "reserves" })}
          />
        ) : (
          library
        );

      case "reserves":
        return stockroom ? <Reserves vocabulary={vocabulary} onOpen={openItem} /> : library;

      case "receive":
        return stockroom ? <Receive taxonomy={taxonomy} /> : library;

      case "clients":
        return stockroom ? (
          <Clients
            key={route.id ?? "list"}
            id={route.id}
            onOpen={(id) => go({ name: "clients", id })}
            onItem={openItem}
            onBack={() => go({ name: "clients", id: null })}
          />
        ) : (
          library
        );

      case "billing":
        return stockroom ? <Billing /> : library;

      case "capture":
        return <Capture taxonomy={taxonomy} onSaved={openItem} onDone={back} onStorage={setUsedBytes} />;

      case "item":
        return (
          <ItemScreen
            id={route.id}
            taxonomy={taxonomy}
            vocabulary={vocabulary}
            onBack={back}
            onGone={() => go({ name: "home" })}
            onEnrich={() => go({ name: "enrich", ids: [route.id] })}
            onListing={() => go({ name: "listing", id: route.id })}
            onLabel={() => go({ name: "labels", kind: "item", ids: [route.id] })}
            onStorage={setUsedBytes}
            stock={stockroom}
          />
        );

      case "listing":
        return <Listing id={route.id} taxonomy={taxonomy} onItem={openItem} />;

      case "locations":
        return (
          <Locations
            id={route.id}
            taxonomy={taxonomy}
            vocabulary={vocabulary}
            onOpen={openItem}
            onGo={(id) => go({ name: "locations", id })}
            onLabels={(id) => go({ name: "labels", kind: "location", ids: [id] })}
            onSort={() => go({ name: "sort" })}
            stock={stockroom}
          />
        );

      case "sort":
        return (
          <Sort taxonomy={taxonomy} onOpen={openItem} onBack={() => go({ name: "locations", id: null })} />
        );

      case "enrich":
        return (
          <Enrich
            taxonomy={taxonomy}
            vocabulary={vocabulary}
            onOpen={openItem}
            // Back when it was opened from that item, so the back button does not
            // return here; forward to it when it was opened some other way.
            onSaved={(id) => (cameFrom() === pathOf({ name: "item", id }) ? back() : openItem(id))}
            onSettings={() => go({ name: "settings" })}
          />
        );

      default:
        return library;
    }
  }
}
